<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import LabelText from '~/components/form-elements/LabelText.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import YesNoSwitch from '~/components/form-elements/YesNoSwitch.vue'
import constants from '~/constants'
import BankCardType from '~/enums/bank-card-type'
import OrderPaymentMethod from '~/enums/order-payment-method'
import OrderShippingType from '~/enums/order-shipping-type'
import UserPermission from '~/enums/user-permission'
import helpers from '~/helpers'
import { useCartStore } from '~/store/cart'
import { useUserStore } from '~/store/user'
import type { OrderPaymentMethod as CartOrderPaymentMethod } from '~/types/cart'
import type { RadioButtonChoice } from '~/types/form-elements'

const emit = defineEmits<{
  (e: 'payForOrder'): void
}>()

const cartStore = useCartStore()
const userStore = useUserStore()

const isRussianBankCard = ref(true)

const orderPaymentMethodChoices = computed(() => {
  const orderPaymentMethodIds = cartStore.orderPaymentMethods.map((x: CartOrderPaymentMethod): string => x.id)
  const bankCardTypeForOrderShippingType = OrderShippingType.getBankCardType(
    cartStore.orderShippingType as OrderShippingType
  )

  return OrderPaymentMethod
    .sortedMembers
    .filter(
      (x: OrderPaymentMethod): boolean => {
        if (!orderPaymentMethodIds.includes(x)) {
          return false
        }

        const bankCardTypeForOrderPaymentMethod = OrderPaymentMethod.getBankCardType(x)

        if (
          bankCardTypeForOrderShippingType !== BankCardType.Any
          && bankCardTypeForOrderPaymentMethod !== BankCardType.Any
          && bankCardTypeForOrderShippingType !== bankCardTypeForOrderPaymentMethod
        ) {
          return false
        }

        if (bankCardTypeForOrderShippingType === BankCardType.Any
          && (
            (isRussianBankCard.value && bankCardTypeForOrderPaymentMethod === BankCardType.International)
            || (!isRussianBankCard.value && bankCardTypeForOrderPaymentMethod === BankCardType.Russian)
          )
        ) {
          return false
        }

        const orderPaymentMethod = cartStore.orderPaymentMethods.find((y: CartOrderPaymentMethod): boolean => y.id === x)

        if (!orderPaymentMethod) {
          return false
        }

        if (
          typeof orderPaymentMethod.maxOrderTotalAmountInKopecks === 'number'
          && cartStore.totalInKopecks > orderPaymentMethod.maxOrderTotalAmountInKopecks
        ) {
          return false
        }

        if (
          typeof orderPaymentMethod.minOrderTotalAmountInKopecks === 'number'
          && cartStore.totalInKopecks < orderPaymentMethod.minOrderTotalAmountInKopecks
        ) {
          return false
        }

        return true
      }
    )
    .map(
      (x: OrderPaymentMethod): RadioButtonChoice => ({
        iconDescription: OrderPaymentMethod.getIconDescription(x),
        iconName: OrderPaymentMethod.getIconName(x),
        labelText: OrderPaymentMethod.getName(x),
        value: x
      })
    )
})

watch(
  orderPaymentMethodChoices,
  (x: RadioButtonChoice[]): void => {
    cartStore.orderPaymentMethod = x.length ? x[0].value as OrderPaymentMethod : null
  }
)
</script>

<template>
  <BlockWithBorder>
    <div class="h4">
      Оплата
    </div>

    <YesNoSwitch
      v-if="cartStore.orderShippingType !== null &&
        OrderShippingType.getBankCardType(cartStore.orderShippingType) === BankCardType.Any"
      v-model="isRussianBankCard"
      class="mb-6"
      :label-text="'У\xA0меня российская карта'"
    />

    <template v-if="orderPaymentMethodChoices.length">
      <Notification
        v-if="cartStore.orderShippingType !== null && (
          OrderShippingType.getBankCardType(cartStore.orderShippingType) === BankCardType.Any && !isRussianBankCard ||
          OrderShippingType.getBankCardType(cartStore.orderShippingType) === BankCardType.International
        )"
        class="mb-6"
        is-small
        status="warning"
      >
        Если ваша карта выпущена не&nbsp;в&nbsp;России, выберите способ оплаты, поддерживающий её&nbsp;платёжную систему (см. зарубежные карты), иначе платёж не пройдёт.
      </Notification>

      <RadioButtons
        v-model="cartStore.orderPaymentMethod"
        :choices="orderPaymentMethodChoices"
        class="mb-6"
        is-vertical
        label-text="Способ оплаты"
        name="orderPaymentMethod"
        with-blocks
      />

      <Notification
        v-if="cartStore.orderPaymentMethod === OrderPaymentMethod.Dolyame"
        class="mb-6"
        is-small
        status="warning"
      >
        Внимание! Отменить заказ, оплаченный через «Долями», невозможно.
      </Notification>

      <LabelText class="mb-1">
        <template v-if="cartStore.orderPaymentMethod === OrderPaymentMethod.Dolyame">
          Я&nbsp;принимаю условия <a :href="constants.documentUrls.userAgreement" target="_blank">пользовательского соглашения</a>, <a href="https://static.dolyame.ru/static/dolyame/dolyame_contract.pdf" target="_blank">договора «Долями»</a> и&nbsp;даю своё <a :href="constants.documentUrls.dataProtectionPolicy" target="_blank">согласие на&nbsp;обработку персональных данных</a>.
        </template>
        <template v-else>
          Я&nbsp;принимаю условия <a :href="constants.documentUrls.userAgreement" target="_blank">пользовательского соглашения</a> и&nbsp;даю своё <a :href="constants.documentUrls.dataProtectionPolicy" target="_blank">согласие на&nbsp;обработку персональных данных</a>.
        </template>
      </LabelText>
      <YesNoSwitch v-model="cartStore.form.isAgreeToTermsAndConditions" class="mb-6" />

      <template v-if="cartStore.preOrderCartProductsData.length">
        <LabelText class="mb-1">
          <template v-if="cartStore.preOrderCartProductsData.length === 1">
            Даю своё согласие на&nbsp;отправку всего заказа после поступления <a :href="helpers.getProductPath(cartStore.preOrderCartProductsData[0])" target="_blank">товара по&nbsp;предзаказу</a> на&nbsp;склад.
          </template>
          <template v-else>
            Даю своё согласие на&nbsp;отправку всего заказа после поступления товаров по&nbsp;предзаказу на&nbsp;склад.
          </template>
        </LabelText>
        <YesNoSwitch v-model="cartStore.form.isAgreeToPreOrder" class="mb-6" />
      </template>

      <YesNoSwitch
        v-if="userStore.hasPermission(UserPermission.CreateBloggerOrder)"
        v-model="cartStore.isBloggerOrder"
        class="mb-6"
        label-text="Блогерский заказ"
      />

      <YesNoSwitch
        v-if="userStore.hasPermission(UserPermission.CreateRepeatOrder)"
        v-model="cartStore.isRepeatOrder"
        class="mb-6"
        label-text="Повторный заказ"
      />

      <BasicButton
        class="mb-6"
        color="green"
        :is-loading="cartStore.isOrderCreating"
        title="Оплатить заказ"
        @click="emit('payForOrder')"
      />

      <p class="mb-0 text-sm">
        Нажав кнопку «Оплатить заказ», вы&nbsp;попадёте на&nbsp;защищённую страницу оплаты.
      </p>
    </template>
    <Notification v-else status="warning">
      Нет доступных способов оплаты.
    </Notification>
  </BlockWithBorder>
</template>
